import React from "react";
import { useTheme } from "../../contexts/ThemeContext";

interface PaginationProps {
    page: number;
    total_pages: number;
    total_records: number;
    page_size: number;
    onPageChange: (page: number) => void;
    page_size_options?: number[];
    onPageSizeChange?: (page_size: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
    page,
    total_pages,
    total_records,
    page_size,
    onPageChange,
    page_size_options,
    onPageSizeChange
}) => {
    const { effectiveTheme } = useTheme();
    const isDark = effectiveTheme === "dark";

    // Function to generate page numbers with "..."
    const generatePages = () => {
        const pages: (number | string)[] = [];

        if (total_pages <= 7) {
            for (let i = 1; i <= total_pages; i++) pages.push(i);
            return pages;
        }

        pages.push(1);

        if (page > 3) pages.push("...");

        const start = Math.max(2, page - 1);
        const end = Math.min(total_pages - 1, page + 1);

        for (let i = start; i <= end; i++) pages.push(i);

        if (page < total_pages - 2) pages.push("...");

        pages.push(total_pages);

        return pages;
    };

    const borderColor = isDark ? "rgba(148, 163, 184, 0.25)" : "#e2e8f0";
    const surfaceColor = isDark ? "rgba(148, 163, 184, 0.08)" : "#fff";
    const textColor = isDark ? "#e2e8f0" : "#334155";
    const mutedColor = isDark ? "#94a3b8" : "#64748b";
    const disabledColor = isDark ? "#475569" : "#cbd5e1";

    const navButtonStyle = (disabled: boolean): React.CSSProperties => ({
        padding: "8px 16px",
        fontSize: "13px",
        fontWeight: 600,
        borderRadius: "10px",
        border: `1px solid ${borderColor}`,
        background: surfaceColor,
        color: disabled ? disabledColor : textColor,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.15s ease",
    });

    return (
        <div style={{
            marginTop: "8px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
        }}>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    flexWrap: "wrap",
                }}
            >
                <div
                    style={{
                        fontSize: "13px",
                        color: mutedColor,
                    }}
                >
                    <strong style={{ color: textColor }}>
                        Total {Math.min(page * page_size, total_records)} of {total_records}{" "}
                        records found
                    </strong>
                </div>

                {onPageSizeChange && (
                    <label
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "13px",
                            color: mutedColor,
                        }}
                    >
                        Show
                        <select
                            value={page_size}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                            style={{
                                padding: "8px 10px",
                                fontSize: "13px",
                                fontWeight: 600,
                                borderRadius: "10px",
                                border: "1px solid #2563eb",
                                background: surfaceColor,
                                color: "#2563eb",
                                cursor: "pointer",
                                outline: "none",
                            }}
                        >
                            {(page_size_options ?? [10, 25, 50, 100]).map((size) => (
                                <option
                                    key={size}
                                    value={size}
                                    style={{ background: "#fff", color: "#2563eb" }}
                                >
                                    {size}
                                </option>
                            ))}
                        </select>
                        per page
                    </label>
                )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                {/* Prev Button */}
                <button
                    disabled={page <= 1}
                    onClick={() => onPageChange(page - 1)}
                    style={navButtonStyle(page <= 1)}
                >
                    Prev
                </button>

                {/* Number Buttons */}
                {generatePages().map((p, i) =>
                    p === "..." ? (
                        <span key={i} style={{ padding: "6px 6px", color: mutedColor, fontSize: "13px" }}>...</span>
                    ) : (
                        <button
                            key={i}
                            onClick={() => onPageChange(p as number)}
                            style={{
                                minWidth: "36px",
                                padding: "8px 10px",
                                fontSize: "13px",
                                fontWeight: 600,
                                background: p === page ? "#2563eb" : surfaceColor,
                                color: p === page ? "#fff" : textColor,
                                border: p === page ? "1px solid #2563eb" : `1px solid ${borderColor}`,
                                borderRadius: "10px",
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                            }}
                        >
                            {p}
                        </button>
                    )
                )}

                {/* Next Button */}
                <button
                    disabled={page >= total_pages}
                    onClick={() => onPageChange(page + 1)}
                    style={navButtonStyle(page >= total_pages)}
                >
                    Next
                </button>
            </div>
        </div>
    );
};

export default Pagination;
